/**
 * ConversionDrillPanel - Right-side slide-out panel for conversion drill-down
 *
 * Handles two modes:
 * 1. Funnel stage drill-down — drop-off analysis, channel contribution, contextual insights
 * 2. Channel conversion drill-down — rate comparison, quality assessment
 *
 * Style: Solid colors only. NO gradients, NO glows, NO glassmorphism.
 * Uses bg-bg-1, border-border, font-mono for metrics.
 */

import { useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  X,
  TrendingDown,
  BarChart3,
  Lightbulb,
  Award,
  MessageSquare,
} from 'lucide-react';
import { useChatStore, PANEL_WIDTH_PX } from '@/stores/chatStore';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FunnelStageData {
  type: 'funnel';
  label: string;
  value: number;
  pctOfTop: number;
  prevStageValue?: number;
  prevStageLabel?: string;
}

export interface ChannelConversionData {
  type: 'channel';
  channel: string;
  keyEvents: number;
  sessions: number;
  rate: number;
}

export type ConversionDrillData = FunnelStageData | ChannelConversionData;

interface ConversionDrillPanelProps {
  data: ConversionDrillData | null;
  open: boolean;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const KEY_EVENTS_BY_CHANNEL = [
  { channel: 'Paid Social', keyEvents: 51, pct: 43 },
  { channel: 'Paid Search', keyEvents: 24, pct: 20 },
  { channel: 'Direct', keyEvents: 17, pct: 14 },
  { channel: 'Organic Search', keyEvents: 16, pct: 14 },
  { channel: 'Referral', keyEvents: 5, pct: 4 },
  { channel: 'Organic Social', keyEvents: 3, pct: 3 },
  { channel: 'Paid Other', keyEvents: 2, pct: 2 },
];

const STAGE_INSIGHTS: Record<string, string> = {
  'Total Sessions':
    'This is the top of your funnel — all visitors who started a session.',
  'Engaged Sessions':
    'These users stayed long enough to be considered engaged (>10s, >2 pageviews, or a key event).',
  'Reservation Button Clicks':
    'Users who clicked the reservation button — strong booking intent signal.',
  'Form Submissions':
    'Users who completed the homepage form — your primary conversion event.',
};

const OVERALL_AVG_RATE = 0.13;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SectionHeader({
  icon: Icon,
  title,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
}) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-text-3" />
      <h4 className="text-xs font-semibold uppercase tracking-wider text-text-3">
        {title}
      </h4>
    </div>
  );
}

function MetricCard({
  label,
  value,
  colorClass = 'text-text',
}: {
  label: string;
  value: string;
  colorClass?: string;
}) {
  return (
    <div className="px-4 py-3 rounded-none bg-bg-1 border border-border">
      <div className="text-xs text-text-3 uppercase tracking-wider">
        {label}
      </div>
      <div className={`text-lg font-mono font-bold mt-1 ${colorClass}`}>
        {value}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Funnel Stage Content
// ---------------------------------------------------------------------------

function FunnelStageContent({ data }: { data: FunnelStageData }) {
  const dropOff =
    data.prevStageValue != null
      ? ((data.prevStageValue - data.value) / data.prevStageValue) * 100
      : null;

  const usersLost =
    data.prevStageValue != null ? data.prevStageValue - data.value : null;

  const dropOffColor =
    dropOff == null
      ? 'text-text-3'
      : dropOff > 50
        ? 'text-danger'
        : dropOff > 20
          ? 'text-warning'
          : 'text-success';

  const barDropOffColor =
    dropOff == null
      ? 'bg-text-3'
      : dropOff > 90
        ? 'bg-danger'
        : dropOff > 50
          ? 'bg-warning'
          : 'bg-monarch-gold';

  const currentWidthPct =
    data.prevStageValue != null && data.prevStageValue > 0
      ? (data.value / data.prevStageValue) * 100
      : 100;

  return (
    <>
      {/* Hero Metrics */}
      <section>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <MetricCard
            label="Volume"
            value={data.value.toLocaleString()}
          />
          <MetricCard
            label="% of Total Sessions"
            value={`${data.pctOfTop.toFixed(2)}%`}
          />
          <MetricCard
            label="Drop-off from Previous"
            value={dropOff != null ? `${dropOff.toFixed(1)}%` : 'N/A'}
            colorClass={dropOffColor}
          />
        </div>
      </section>

      {/* Drop-off Analysis */}
      {data.prevStageValue != null && dropOff != null && (
        <section>
          <SectionHeader icon={TrendingDown} title="Drop-off Analysis" />
          <div className="space-y-4 bg-bg-1 border border-border rounded-none p-4">
            <div className="flex items-baseline gap-3">
              <span className={`text-2xl font-mono font-bold ${dropOffColor}`}>
                {dropOff.toFixed(1)}% drop-off
              </span>
              {usersLost != null && (
                <span className="text-xs text-text-3">
                  {usersLost.toLocaleString()} users lost
                </span>
              )}
            </div>

            {/* Visual bars */}
            <div className="space-y-2">
              <div>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-text-2">
                    {data.prevStageLabel ?? 'Previous Stage'}
                  </span>
                  <span className="font-mono text-text-3">
                    {data.prevStageValue.toLocaleString()}
                  </span>
                </div>
                <div className="h-3 bg-bg-2 rounded-none overflow-hidden">
                  <div
                    className="h-full bg-monarch-gold rounded-none"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-text-2">{data.label}</span>
                  <span className="font-mono text-text-3">
                    {data.value.toLocaleString()}
                  </span>
                </div>
                <div className="h-3 bg-bg-2 rounded-none overflow-hidden">
                  <div
                    className={`h-full rounded-none ${barDropOffColor}`}
                    style={{ width: `${currentWidthPct}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Channel Contribution */}
      <section>
        <SectionHeader icon={BarChart3} title="Channel Contribution" />
        <div className="space-y-2">
          {KEY_EVENTS_BY_CHANNEL.map((ch) => (
            <div
              key={ch.channel}
              className="bg-bg-1 border border-border rounded-none p-3"
            >
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-text-2 font-medium">{ch.channel}</span>
                <span className="font-mono text-text-3">
                  {ch.keyEvents} events ({ch.pct}%)
                </span>
              </div>
              <div className="h-1.5 bg-bg-2 rounded-none overflow-hidden">
                <div
                  className="h-full bg-monarch-gold rounded-none"
                  style={{ width: `${ch.pct}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* What This Means */}
      <section>
        <SectionHeader icon={Lightbulb} title="What This Means" />
        <div className="bg-monarch-gold/5 border border-monarch-gold/20 rounded-lg p-3">
          <p className="text-sm text-text-2 leading-relaxed">
            {STAGE_INSIGHTS[data.label] ??
              `This stage represents ${data.pctOfTop.toFixed(2)}% of total sessions. Analyze user behavior at this point to identify optimization opportunities.`}
          </p>
        </div>
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Channel Conversion Content
// ---------------------------------------------------------------------------

function ChannelConversionContent({ data }: { data: ChannelConversionData }) {
  const rateColor =
    data.rate >= 0.2
      ? 'text-success'
      : data.rate >= 0.1
        ? 'text-warning'
        : 'text-danger';

  const diff = data.rate - OVERALL_AVG_RATE;
  const aboveBelow = diff >= 0 ? 'above' : 'below';
  const diffColor = diff >= 0 ? 'text-success' : 'text-danger';

  const rateBarWidth = Math.min((data.rate / 0.5) * 100, 100);
  const avgBarWidth = Math.min((OVERALL_AVG_RATE / 0.5) * 100, 100);

  // Quality tier
  let tierLabel: string;
  let tierBadgeClass: string;
  let tierDescription: string;

  if (data.rate >= 0.3) {
    tierLabel = 'Top Performer';
    tierBadgeClass =
      'bg-success/10 text-success border border-success/20';
    tierDescription =
      'This channel converts at an exceptional rate. Consider increasing budget allocation to maximize returns.';
  } else if (data.rate >= 0.15) {
    tierLabel = 'Above Average';
    tierBadgeClass =
      'bg-monarch-gold/10 text-monarch-gold border border-monarch-gold/20';
    tierDescription =
      'Solid conversion performance. This channel warrants sustained or moderately increased investment.';
  } else if (data.rate >= 0.08) {
    tierLabel = 'Average';
    tierBadgeClass = 'bg-bg-2 text-text-3 border border-border';
    tierDescription =
      'Conversion rate is in line with the overall average. Explore creative and targeting optimizations to improve.';
  } else {
    tierLabel = 'Below Average';
    tierBadgeClass =
      'bg-danger/10 text-danger border border-danger/20';
    tierDescription =
      'This channel underperforms relative to others. Evaluate whether to optimize targeting, reduce spend, or reallocate budget.';
  }

  return (
    <>
      {/* Hero Metrics */}
      <section>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <MetricCard
            label="Key Events"
            value={data.keyEvents.toLocaleString()}
          />
          <MetricCard
            label="Sessions"
            value={data.sessions.toLocaleString()}
          />
          <MetricCard
            label="Conversion Rate"
            value={`${data.rate.toFixed(2)}%`}
            colorClass={rateColor}
          />
        </div>
      </section>

      {/* Rate Comparison */}
      <section>
        <SectionHeader icon={BarChart3} title="Rate Comparison" />
        <div className="bg-bg-1 border border-border rounded-none p-4 space-y-4">
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-text-2 font-medium">{data.channel}</span>
                <span className="font-mono text-text">
                  {data.rate.toFixed(2)}%
                </span>
              </div>
              <div className="h-2 bg-bg-2 rounded-none overflow-hidden">
                <div
                  className="h-full bg-monarch-gold rounded-none"
                  style={{ width: `${rateBarWidth}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-text-3">Overall Average</span>
                <span className="font-mono text-text-3">
                  {OVERALL_AVG_RATE.toFixed(2)}%
                </span>
              </div>
              <div className="h-2 bg-bg-2 rounded-none overflow-hidden">
                <div
                  className="h-full bg-text-3 rounded-none"
                  style={{ width: `${avgBarWidth}%` }}
                />
              </div>
            </div>
          </div>

          <div className="pt-2 border-t border-border">
            <span className={`text-sm font-mono font-semibold ${diffColor}`}>
              {Math.abs(diff).toFixed(2)}% {aboveBelow} average
            </span>
          </div>
        </div>
      </section>

      {/* Quality Assessment */}
      <section>
        <SectionHeader icon={Award} title="Quality Assessment" />
        <div className="bg-bg-1 border border-border rounded-none p-4 space-y-3">
          <span
            className={`inline-flex px-2 py-1 rounded text-xs font-semibold uppercase ${tierBadgeClass}`}
          >
            {tierLabel}
          </span>
          <p className="text-sm text-text-2 leading-relaxed">
            {tierDescription}
          </p>
        </div>
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main Panel
// ---------------------------------------------------------------------------

export default function ConversionDrillPanel({
  data,
  open,
  onClose,
}: ConversionDrillPanelProps) {
  const btnRef = useRef<HTMLButtonElement>(null);

  // Close on Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, handleKeyDown]);

  const chatIsOpen = useChatStore((s) => s.isOpen);
  const chatPanelWidth = useChatStore((s) => s.panelWidth);
  const rightOffset = chatIsOpen ? PANEL_WIDTH_PX[chatPanelWidth] : 0;

  if (!open) return null;

  const title =
    data?.type === 'funnel'
      ? data.label
      : data?.type === 'channel'
        ? data.channel
        : 'Conversion Details';

  const askAiQuery =
    data?.type === 'funnel'
      ? `Analyze the '${data.label}' funnel stage: ${data.value.toLocaleString()} (${data.pctOfTop}% of total). What drives users to this stage and how can we improve progression to the next?`
      : data?.type === 'channel'
        ? `${data.channel} has ${data.keyEvents} key events from ${data.sessions.toLocaleString()} sessions (${data.rate}% rate). How does this channel's conversion quality compare and what should we optimize?`
        : '';

  const handleAskAi = () => {
    if (!btnRef.current || !askAiQuery) return;
    useChatStore
      .getState()
      .showAnalyzePrompt(
        askAiQuery,
        '',
        btnRef.current.getBoundingClientRect(),
      );
  };

  return (
    <>
      {/* Backdrop */}
      <motion.div
        className="fixed inset-0 z-40 bg-black/70"
        style={rightOffset > 0 ? { right: rightOffset } : undefined}
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        aria-hidden
      />

      {/* Slide-out panel */}
      <motion.div
        className="fixed top-0 z-50 h-full w-full sm:max-w-2xl bg-bg-0 border-l border-border"
        style={{ right: rightOffset }}
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        role="dialog"
        aria-modal
        aria-label={title}
      >
        {/* Sticky Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-border bg-bg-1">
          <h2 className="font-display text-lg font-bold text-text truncate pr-4">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-none bg-bg-2 border border-border hover:border-border-2 transition-colors text-text-3 hover:text-text flex-shrink-0"
            aria-label="Close panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto h-[calc(100%-60px)] px-4 sm:px-6 py-4 sm:py-6 space-y-6 sm:space-y-8">
          {!data ? (
            <p className="text-xs text-text-3 italic">No data available.</p>
          ) : data.type === 'funnel' ? (
            <FunnelStageContent data={data} />
          ) : (
            <ChannelConversionContent data={data} />
          )}

          {/* Ask AI Button */}
          {data && (
            <section className="pb-4">
              <button
                ref={btnRef}
                onClick={handleAskAi}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-monarch-gold/10 border border-monarch-gold/30 rounded-none text-sm font-medium text-monarch-gold hover:bg-monarch-gold/20 hover:border-monarch-gold/50 transition-colors"
              >
                <MessageSquare className="w-4 h-4" />
                Ask AI about this
              </button>
            </section>
          )}
        </div>
      </motion.div>
    </>
  );
}
